import { mkdir, readFile, writeFile } from "node:fs/promises";
import path from "node:path";

import { ConfigurationError } from "./errors.js";
import { assertStorageRootReady, resolveStorageRoot, globalConfigPath } from "./mentis-home.js";
import { PI_VERSION, isPiVersionSupported } from "./compatibility.js";

export interface EmbeddingBatchPolicy {
  readonly maxItems: number;
  readonly maxTokens: number;
  readonly maxWaitMs: number;
  readonly maxConcurrentBatches: number;
}

export interface SiliconFlowConfig {
  readonly baseUrl: string;
  readonly apiKeyEnv: string;
  readonly embedding: {
    readonly model: string;
    readonly dimensions: number;
    readonly encodingFormat: "float" | "base64";
    readonly truncate: "left" | "right" | "reject";
  };
  readonly rerank: {
    readonly model: string;
    readonly maxInputTokens: number;
    readonly instruction?: string;
    readonly returnDocuments: false;
    readonly maxChunksPerDoc?: number;
    readonly overlapTokens?: number;
  };
  readonly timeout: {
    readonly embeddingMs: number;
    readonly rerankMs: number;
  };
  readonly retry: {
    readonly maxAttempts: number;
    readonly baseDelayMs: number;
    readonly maxDelayMs: number;
  };
  readonly rateLimits: {
    readonly concurrentRequests: number;
    readonly requestsPerSecond?: number;
    readonly tokensPerMinute?: number;
    readonly circuitFailureThreshold: number;
    readonly circuitOpenMs: number;
  };
}

export interface OpenRouterConfig {
  readonly baseUrl: string;
  readonly apiKeyEnv: string;
  readonly embedding: {
    readonly model: string;
    readonly dimensions: number;
    readonly encodingFormat: "float" | "base64";
    readonly truncate: "left" | "right" | "reject";
  };
  readonly timeout: {
    readonly embeddingMs: number;
  };
  readonly retry: {
    readonly maxAttempts: number;
    readonly baseDelayMs: number;
    readonly maxDelayMs: number;
  };
  readonly rateLimits: {
    readonly concurrentRequests: number;
    readonly requestsPerSecond?: number;
    readonly tokensPerMinute?: number;
    readonly circuitFailureThreshold: number;
    readonly circuitOpenMs: number;
  };
}

export interface RuntimeConfig {
  readonly piVersion: string;
}

export interface KnowledgeConfig {
  readonly enabled: boolean;
  readonly defaultNamespace: string;
  readonly autoSync: boolean;
}

export interface MemoryConfig {
  readonly enabled: boolean;
  readonly captureEnabled: boolean;
  readonly turnEventLimit: number;
  readonly offload: {
    readonly inlineMaxBytes: number;
    readonly truncateMaxBytes: number;
    readonly previewBytes: number;
  };
}

export interface RetrievalConfig {
  readonly automaticRecall: boolean;
  readonly automaticRecallTokens: number;
  readonly totalAutomaticContextTokens: number;
  readonly autoRecallSoftTimeoutMs: number;
  readonly autoRecallHardTimeoutMs: number;
  readonly manualSearchTimeoutMs: number;
  readonly maxManualSearchTimeoutMs: number;
  readonly contextTokens: number;
  readonly knowledgeTokens: number;
  readonly memoryTokens: number;
}

export interface InferenceConfig {
  readonly provider: "siliconflow" | "openrouter";
  readonly siliconflow: SiliconFlowConfig;
  readonly openrouter: OpenRouterConfig;
  readonly embedding: {
    readonly required: true;
    readonly queryCacheEntries: number;
    readonly queryCacheTtlMs: number;
    readonly batch: EmbeddingBatchPolicy;
  };
  readonly rerank: {
    readonly enabled: boolean;
    readonly required: boolean;
    readonly candidateLimit: number;
    readonly fallbackEnabled: boolean;
    readonly cacheEntries: number;
    readonly cacheTtlMs: number;
  };
}

export interface ResourceLimits {
  readonly maxFileBytes: number;
  readonly maxWebPages: number;
  readonly maxWebBytes: number;
  readonly maxArchiveBytes: number;
  readonly maxArchiveEntries: number;
  readonly maxExpandedBytes: number;
  readonly maxDocumentTokens: number;
  readonly maxConcurrentParsers: number;
  readonly maxPendingEmbeddingTokens: number;
  readonly maxPendingRerankTokens: number;
  readonly maxRerankDocuments: number;
}

export interface StorageConfig {
  readonly rootDir: string;
  readonly readOnly: boolean;
  readonly lockTimeoutMs: number;
  readonly generationRetentionMs: number;
  readonly writeBatch: {
    readonly maxOperations: number;
    readonly maxBytes: number;
    readonly maxWaitMs: number;
  };
}

export interface PerformanceConfig {
  readonly cpuWorkers: number;
  readonly ioConcurrency: number;
  readonly providerConcurrency: number;
  readonly sidecar: {
    /** Unix nice value applied best-effort to the isolated Sidecar process. */
    readonly cpuNice: number;
    /** Maximum durable knowledge-ingest jobs executing at once. */
    readonly knowledgeJobConcurrency: number;
    /** Quiet delay before disk/CPU-heavy maintenance begins after a settled turn. */
    readonly maintenanceDelayMs: number;
  };
  readonly queue: {
    readonly maxQueuedTasks: number;
    readonly maxQueuedBytes: number;
    readonly maxActiveTasks: number;
    readonly maxQueuedTaskAgeMs: number;
    readonly maxPendingEmbeddingTokens: number;
    readonly maxPendingRerankTokens: number;
  };
  readonly resources: ResourceLimits;
}

export interface ObservabilityConfig {
  readonly enabled: boolean;
  readonly includeContentHashes: boolean;
  readonly logLevel: "silent" | "error" | "warn" | "info" | "debug";
}

export interface IntelligenceConfig {
  readonly workingMemory: {
    readonly enabled: boolean;
    readonly promptTokens: number;
    readonly hardMaxTokens: number;
    readonly maxConfirmed: number;
    readonly maxHypotheses: number;
    readonly maxOpenLoops: number;
    readonly maxRecentOutcomes: number;
    readonly maxActiveResources: number;
  };
  readonly memoryFormation: {
    readonly enabled: boolean;
    readonly autoPromotion: boolean;
    readonly maxCandidatesPerTurn: number;
    readonly candidateMaxCharacters: number;
    readonly maxInputTokens: number;
    readonly maxOutputTokens: number;
    readonly candidateTtlMs: number;
    readonly minimumPreferenceObservations: number;
    readonly minimumBehaviorObservations: number;
  };
  readonly consolidation: {
    readonly enabled: boolean;
    readonly maxDigestTokens: number;
    readonly maxOutputTokens: number;
    readonly maxSemanticCandidates: number;
    readonly longTaskCheckpointTurns: number;
    readonly procedureMinimumOutcomes: number;
    readonly procedureMinimumSuccessEstimate: number;
  };
  readonly context: {
    readonly persistSnapshots: boolean;
    readonly capabilityMaxAgeMs: number;
  };
  readonly temporal: {
    readonly enabled: true;
  };
  readonly views: {
    readonly enabled: boolean;
    readonly ttlMs: number;
  };
  readonly effectiveness: {
    readonly enabled: boolean;
    readonly flushIntervalMs: number;
    readonly maxBatch: number;
  };
  readonly adaptivePolicy: {
    readonly enabled: boolean;
    readonly cooldownMs: number;
  };
}

export interface PiMentisConfig {
  readonly runtime: RuntimeConfig;
  readonly knowledge: KnowledgeConfig;
  readonly memory: MemoryConfig;
  readonly retrieval: RetrievalConfig;
  readonly inference: InferenceConfig;
  readonly storage: StorageConfig;
  readonly performance: PerformanceConfig;
  readonly observability: ObservabilityConfig;
  readonly intelligence: IntelligenceConfig;
}

export interface EmbeddingRuntimeResolution {
  readonly configured: Readonly<{
    model: string;
    dimensions: number;
  }>;
  readonly effective: Readonly<{
    providerId: "siliconflow" | "openrouter";
    model: string;
    dimensions: number;
  }>;
  /** Where the effective embedding model was selected. */
  readonly source: "config" | "environment" | "default";
  /** True only when an environment fallback actually selected the effective model. */
  readonly environmentOverrideActive: boolean;
}

export interface ActiveEmbeddingConfig {
  readonly providerId: "siliconflow" | "openrouter";
  readonly baseUrl: string;
  readonly apiKeyEnv: string;
  readonly embedding: OpenRouterConfig["embedding"];
}

export interface EmbeddingStorageIdentity {
  readonly providerId: string;
  readonly modelId: string;
  readonly dimensions: number;
}

export function getActiveEmbeddingConfig(config: PiMentisConfig): ActiveEmbeddingConfig {
  const providerId = config.inference.provider;
  const provider = config.inference[providerId];
  return {
    providerId,
    baseUrl: provider.baseUrl,
    apiKeyEnv: provider.apiKeyEnv,
    embedding: provider.embedding,
  };
}

/**
 * Stable vector-space identity used by persisted generations. OpenRouter can
 * route the same verified model exposed by SiliconFlow, so those known model
 * aliases deliberately retain the legacy SiliconFlow generation identity.
 */
export function getEmbeddingStorageIdentity(config: PiMentisConfig): EmbeddingStorageIdentity {
  const active = getActiveEmbeddingConfig(config);
  if (active.providerId === "openrouter") {
    if (active.embedding.model === "qwen/qwen3-embedding-8b") {
      return {
        providerId: "siliconflow",
        modelId: "Qwen/Qwen3-Embedding-8B",
        dimensions: active.embedding.dimensions,
      };
    }
    if (active.embedding.model === "baai/bge-m3") {
      return {
        providerId: "siliconflow",
        modelId: "BAAI/bge-m3",
        dimensions: active.embedding.dimensions,
      };
    }
  }
  return {
    providerId: active.providerId,
    modelId: active.embedding.model,
    dimensions: active.embedding.dimensions,
  };
}

const embeddingRuntimeResolutions = new WeakMap<PiMentisConfig, EmbeddingRuntimeResolution>();

export function getEmbeddingRuntimeResolution(config: PiMentisConfig): EmbeddingRuntimeResolution {
  const active = getActiveEmbeddingConfig(config);
  return (
    embeddingRuntimeResolutions.get(config) ?? {
      configured: {
        model: active.embedding.model,
        dimensions: active.embedding.dimensions,
      },
      effective: {
        providerId: active.providerId,
        model: active.embedding.model,
        dimensions: active.embedding.dimensions,
      },
      source: "default",
      environmentOverrideActive: false,
    }
  );
}

export function createDefaultConfig(
  cwd: string,
  environment: NodeJS.ProcessEnv = process.env,
): PiMentisConfig {
  void cwd;
  const storageRoot = resolveStorageRoot({ environment });
  const availableProcessors = globalThis.navigator?.hardwareConcurrency ?? 2;
  return {
    runtime: { piVersion: PI_VERSION },
    knowledge: {
      enabled: true,
      defaultNamespace: "user",
      autoSync: false,
    },
    memory: {
      enabled: true,
      captureEnabled: true,
      turnEventLimit: 256,
      offload: {
        inlineMaxBytes: 8 * 1024,
        truncateMaxBytes: 64 * 1024,
        previewBytes: 4 * 1024,
      },
    },
    retrieval: {
      automaticRecall: false,
      automaticRecallTokens: 800,
      totalAutomaticContextTokens: 1_200,
      autoRecallSoftTimeoutMs: 25,
      autoRecallHardTimeoutMs: 50,
      manualSearchTimeoutMs: 3_000,
      maxManualSearchTimeoutMs: 10_000,
      contextTokens: 1_600,
      knowledgeTokens: 1_100,
      memoryTokens: 500,
    },
    inference: {
      provider: "siliconflow",
      siliconflow: {
        baseUrl: "https://api.siliconflow.cn/v1",
        apiKeyEnv: "SILICONFLOW_API_KEY",
        embedding: {
          model: "Qwen/Qwen3-Embedding-8B",
          dimensions: 1_024,
          encodingFormat: "float",
          truncate: "reject",
        },
        rerank: {
          model: "Qwen/Qwen3-Reranker-8B",
          maxInputTokens: 32_768,
          returnDocuments: false,
        },
        timeout: {
          embeddingMs: 15_000,
          rerankMs: 15_000,
        },
        retry: {
          maxAttempts: 3,
          baseDelayMs: 200,
          maxDelayMs: 3_000,
        },
        rateLimits: {
          concurrentRequests: 2,
          requestsPerSecond: 10,
          tokensPerMinute: 1_000_000,
          circuitFailureThreshold: 2,
          circuitOpenMs: 3_000,
        },
      },
      openrouter: {
        baseUrl: "https://openrouter.ai/api/v1",
        apiKeyEnv: "OPENROUTER_API_KEY",
        embedding: {
          model: "qwen/qwen3-embedding-8b",
          dimensions: 1_024,
          encodingFormat: "float",
          truncate: "reject",
        },
        timeout: {
          embeddingMs: 15_000,
        },
        retry: {
          maxAttempts: 3,
          baseDelayMs: 200,
          maxDelayMs: 3_000,
        },
        rateLimits: {
          concurrentRequests: 2,
          requestsPerSecond: 10,
          tokensPerMinute: 1_000_000,
          circuitFailureThreshold: 2,
          circuitOpenMs: 3_000,
        },
      },
      embedding: {
        required: true,
        queryCacheEntries: 512,
        queryCacheTtlMs: 300_000,
        batch: {
          maxItems: 32,
          maxTokens: 20_000,
          maxWaitMs: 50,
          maxConcurrentBatches: 2,
        },
      },
      rerank: {
        enabled: true,
        required: false,
        candidateLimit: 40,
        fallbackEnabled: true,
        cacheEntries: 256,
        cacheTtlMs: 60_000,
      },
    },
    storage: {
      rootDir: path.join(storageRoot.zvecRoot),
      readOnly: false,
      lockTimeoutMs: 5_000,
      generationRetentionMs: 7 * 24 * 60 * 60 * 1_000,
      writeBatch: {
        maxOperations: 256,
        maxBytes: 8 * 1024 * 1024,
        maxWaitMs: 20,
      },
    },
    performance: {
      cpuWorkers: Math.max(1, Math.min(4, availableProcessors - 1)),
      ioConcurrency: 8,
      providerConcurrency: 2,
      sidecar: {
        cpuNice: 10,
        knowledgeJobConcurrency: 2,
        maintenanceDelayMs: 5_000,
      },
      queue: {
        maxQueuedTasks: 2_000,
        maxQueuedBytes: 128 * 1024 * 1024,
        maxActiveTasks: 8,
        maxQueuedTaskAgeMs: 30 * 60_000,
        maxPendingEmbeddingTokens: 2_000_000,
        maxPendingRerankTokens: 1_000_000,
      },
      resources: {
        maxFileBytes: 128 * 1024 * 1024,
        maxWebPages: 1_000,
        maxWebBytes: 512 * 1024 * 1024,
        maxArchiveBytes: 512 * 1024 * 1024,
        maxArchiveEntries: 10_000,
        maxExpandedBytes: 2 * 1024 * 1024 * 1024,
        maxDocumentTokens: 2_000_000,
        maxConcurrentParsers: 2,
        maxPendingEmbeddingTokens: 2_000_000,
        maxPendingRerankTokens: 1_000_000,
        maxRerankDocuments: 100,
      },
    },
    observability: {
      enabled: true,
      includeContentHashes: true,
      logLevel: "warn",
    },
    intelligence: {
      workingMemory: {
        enabled: true,
        promptTokens: 900,
        hardMaxTokens: 1_200,
        maxConfirmed: 24,
        maxHypotheses: 12,
        maxOpenLoops: 16,
        maxRecentOutcomes: 20,
        maxActiveResources: 24,
      },
      memoryFormation: {
        enabled: true,
        autoPromotion: false,
        maxCandidatesPerTurn: 3,
        candidateMaxCharacters: 500,
        maxInputTokens: 900,
        maxOutputTokens: 700,
        candidateTtlMs: 30 * 24 * 60 * 60 * 1_000,
        minimumPreferenceObservations: 2,
        minimumBehaviorObservations: 3,
      },
      consolidation: {
        enabled: true,
        maxDigestTokens: 1_600,
        maxOutputTokens: 1_200,
        maxSemanticCandidates: 5,
        longTaskCheckpointTurns: 12,
        procedureMinimumOutcomes: 3,
        procedureMinimumSuccessEstimate: 0.7,
      },
      context: { persistSnapshots: true, capabilityMaxAgeMs: 60_000 },
      temporal: { enabled: true },
      views: { enabled: true, ttlMs: 5 * 60_000 },
      effectiveness: { enabled: true, flushIntervalMs: 250, maxBatch: 64 },
      adaptivePolicy: { enabled: true, cooldownMs: 30 * 60_000 },
    },
  };
}

function requireRange(name: string, value: number, minimum: number, maximum: number): void {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new ConfigurationError(
      `${name} must be a finite number between ${minimum} and ${maximum}; received ${value}`,
      { operation: "configuration-validate", retryable: false },
    );
  }
}

export function validateConfig(config: PiMentisConfig): PiMentisConfig {
  if (config.inference.provider !== "siliconflow" && config.inference.provider !== "openrouter") {
    throw new ConfigurationError(
      `Unsupported inference provider ${String(config.inference.provider)}`,
      {
        operation: "configuration-validate",
        retryable: false,
      },
    );
  }
  if (!isPiVersionSupported(config.runtime.piVersion, PI_VERSION)) {
    throw new ConfigurationError(
      `runtime.piVersion must be at least ${PI_VERSION}; found ${config.runtime.piVersion}`,
      {
        operation: "configuration-validate",
        retryable: false,
      },
    );
  }
  if (config.intelligence.temporal.enabled !== true) {
    throw new ConfigurationError(
      "intelligence.temporal.enabled is a protected truth-safety invariant and must be true",
      { operation: "configuration-validate", retryable: false },
    );
  }
  requireRange(
    "intelligence.workingMemory.promptTokens",
    config.intelligence.workingMemory.promptTokens,
    128,
    config.intelligence.workingMemory.hardMaxTokens,
  );
  requireRange(
    "intelligence.workingMemory.hardMaxTokens",
    config.intelligence.workingMemory.hardMaxTokens,
    128,
    4_096,
  );
  requireRange(
    "intelligence.workingMemory.maxConfirmed",
    config.intelligence.workingMemory.maxConfirmed,
    1,
    256,
  );
  requireRange(
    "intelligence.workingMemory.maxHypotheses",
    config.intelligence.workingMemory.maxHypotheses,
    1,
    128,
  );
  requireRange(
    "intelligence.workingMemory.maxOpenLoops",
    config.intelligence.workingMemory.maxOpenLoops,
    1,
    128,
  );
  requireRange(
    "intelligence.workingMemory.maxRecentOutcomes",
    config.intelligence.workingMemory.maxRecentOutcomes,
    1,
    256,
  );
  requireRange(
    "intelligence.workingMemory.maxActiveResources",
    config.intelligence.workingMemory.maxActiveResources,
    1,
    256,
  );
  requireRange(
    "intelligence.memoryFormation.maxCandidatesPerTurn",
    config.intelligence.memoryFormation.maxCandidatesPerTurn,
    1,
    10,
  );
  requireRange(
    "intelligence.memoryFormation.candidateMaxCharacters",
    config.intelligence.memoryFormation.candidateMaxCharacters,
    80,
    2_000,
  );
  requireRange(
    "intelligence.memoryFormation.maxInputTokens",
    config.intelligence.memoryFormation.maxInputTokens,
    128,
    4_096,
  );
  requireRange(
    "intelligence.memoryFormation.maxOutputTokens",
    config.intelligence.memoryFormation.maxOutputTokens,
    128,
    2_000,
  );
  requireRange(
    "intelligence.memoryFormation.candidateTtlMs",
    config.intelligence.memoryFormation.candidateTtlMs,
    60_000,
    365 * 24 * 60 * 60 * 1_000,
  );
  requireRange(
    "intelligence.memoryFormation.minimumPreferenceObservations",
    config.intelligence.memoryFormation.minimumPreferenceObservations,
    2,
    20,
  );
  requireRange(
    "intelligence.memoryFormation.minimumBehaviorObservations",
    config.intelligence.memoryFormation.minimumBehaviorObservations,
    3,
    50,
  );
  requireRange(
    "intelligence.consolidation.maxDigestTokens",
    config.intelligence.consolidation.maxDigestTokens,
    512,
    8_192,
  );
  requireRange(
    "intelligence.consolidation.maxOutputTokens",
    config.intelligence.consolidation.maxOutputTokens,
    256,
    4_096,
  );
  requireRange(
    "intelligence.consolidation.maxSemanticCandidates",
    config.intelligence.consolidation.maxSemanticCandidates,
    1,
    20,
  );
  requireRange(
    "intelligence.consolidation.longTaskCheckpointTurns",
    config.intelligence.consolidation.longTaskCheckpointTurns,
    2,
    100,
  );
  requireRange(
    "intelligence.consolidation.procedureMinimumOutcomes",
    config.intelligence.consolidation.procedureMinimumOutcomes,
    2,
    20,
  );
  requireRange(
    "intelligence.consolidation.procedureMinimumSuccessEstimate",
    config.intelligence.consolidation.procedureMinimumSuccessEstimate,
    0.5,
    0.99,
  );
  requireRange(
    "inference.siliconflow.embedding.dimensions",
    config.inference.siliconflow.embedding.dimensions,
    768,
    4_096,
  );
  requireRange(
    "inference.openrouter.embedding.dimensions",
    config.inference.openrouter.embedding.dimensions,
    768,
    4_096,
  );
  requireRange(
    "intelligence.context.capabilityMaxAgeMs",
    config.intelligence.context.capabilityMaxAgeMs,
    1_000,
    86_400_000,
  );
  requireRange("intelligence.views.ttlMs", config.intelligence.views.ttlMs, 1_000, 86_400_000);
  requireRange(
    "intelligence.effectiveness.flushIntervalMs",
    config.intelligence.effectiveness.flushIntervalMs,
    10,
    60_000,
  );
  requireRange(
    "intelligence.effectiveness.maxBatch",
    config.intelligence.effectiveness.maxBatch,
    1,
    1_024,
  );
  requireRange(
    "intelligence.adaptivePolicy.cooldownMs",
    config.intelligence.adaptivePolicy.cooldownMs,
    1_000,
    7 * 86_400_000,
  );
  requireRange(
    "inference.siliconflow.rerank.maxInputTokens",
    config.inference.siliconflow.rerank.maxInputTokens,
    8_192,
    32_768,
  );
  requireRange(
    "inference.siliconflow.rateLimits.concurrentRequests",
    config.inference.siliconflow.rateLimits.concurrentRequests,
    1,
    64,
  );
  requireRange(
    "inference.openrouter.rateLimits.concurrentRequests",
    config.inference.openrouter.rateLimits.concurrentRequests,
    1,
    64,
  );
  requireRange(
    "retrieval.autoRecallSoftTimeoutMs",
    config.retrieval.autoRecallSoftTimeoutMs,
    1,
    config.retrieval.autoRecallHardTimeoutMs,
  );
  requireRange(
    "retrieval.automaticRecallTokens",
    config.retrieval.automaticRecallTokens,
    64,
    config.retrieval.contextTokens,
  );
  requireRange(
    "retrieval.totalAutomaticContextTokens",
    config.retrieval.totalAutomaticContextTokens,
    config.intelligence.workingMemory.promptTokens,
    config.retrieval.contextTokens,
  );
  requireRange(
    "retrieval.manualSearchTimeoutMs",
    config.retrieval.manualSearchTimeoutMs,
    1,
    config.retrieval.maxManualSearchTimeoutMs,
  );
  requireRange("memory.offload.inlineMaxBytes", config.memory.offload.inlineMaxBytes, 256, 1e9);
  requireRange(
    "memory.offload.truncateMaxBytes",
    config.memory.offload.truncateMaxBytes,
    config.memory.offload.inlineMaxBytes,
    1e9,
  );
  requireRange("memory.offload.previewBytes", config.memory.offload.previewBytes, 128, 1e7);
  requireRange("performance.sidecar.cpuNice", config.performance.sidecar.cpuNice, -20, 19);
  requireRange(
    "performance.sidecar.knowledgeJobConcurrency",
    config.performance.sidecar.knowledgeJobConcurrency,
    1,
    32,
  );
  requireRange(
    "performance.sidecar.maintenanceDelayMs",
    config.performance.sidecar.maintenanceDelayMs,
    250,
    300_000,
  );
  requireRange(
    "performance.queue.maxQueuedTaskAgeMs",
    config.performance.queue.maxQueuedTaskAgeMs,
    1_000,
    7 * 86_400_000,
  );
  requireRange(
    "performance.resources.maxWebPages",
    config.performance.resources.maxWebPages,
    1,
    10_000,
  );
  requireRange(
    "performance.resources.maxWebBytes",
    config.performance.resources.maxWebBytes,
    config.performance.resources.maxFileBytes,
    config.performance.resources.maxExpandedBytes,
  );
  if (
    config.retrieval.knowledgeTokens + config.retrieval.memoryTokens >
    config.retrieval.contextTokens
  ) {
    throw new ConfigurationError(
      "Knowledge and memory token budgets exceed the total retrieval context budget",
      { operation: "configuration-validate", retryable: false },
    );
  }
  if (!/^[A-Z_][A-Z0-9_]*$/.test(config.inference.siliconflow.apiKeyEnv)) {
    throw new ConfigurationError("siliconflow.apiKeyEnv must name an environment variable", {
      operation: "configuration-validate",
      retryable: false,
    });
  }
  if (!/^[A-Z_][A-Z0-9_]*$/.test(config.inference.openrouter.apiKeyEnv)) {
    throw new ConfigurationError("openrouter.apiKeyEnv must name an environment variable", {
      operation: "configuration-validate",
      retryable: false,
    });
  }
  const baseUrl = new URL(config.inference.siliconflow.baseUrl);
  if (baseUrl.protocol !== "https:" && baseUrl.hostname !== "localhost") {
    throw new ConfigurationError("SiliconFlow base URL must use HTTPS except for localhost", {
      operation: "configuration-validate",
      retryable: false,
    });
  }
  const openRouterBaseUrl = new URL(config.inference.openrouter.baseUrl);
  if (openRouterBaseUrl.protocol !== "https:" && openRouterBaseUrl.hostname !== "localhost") {
    throw new ConfigurationError("OpenRouter base URL must use HTTPS except for localhost", {
      operation: "configuration-validate",
      retryable: false,
    });
  }
  return config;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeConfiguration(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const existing = merged[key];
    merged[key] =
      isRecord(existing) && isRecord(value) ? mergeConfiguration(existing, value) : value;
  }
  return merged;
}

function optionalEnvironmentValue(
  environment: NodeJS.ProcessEnv,
  ...names: readonly string[]
): string | undefined {
  for (const name of names) {
    const value = environment[name]?.trim();
    if (value !== undefined && value !== "") return value;
  }
  return undefined;
}

function optionalEnvironmentInteger(
  environment: NodeJS.ProcessEnv,
  name: string,
): number | undefined {
  const value = optionalEnvironmentValue(environment, name);
  if (value === undefined) return undefined;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) {
    throw new ConfigurationError(`${name} must be an integer; received ${value}`, {
      operation: "configuration-environment",
      retryable: false,
    });
  }
  return parsed;
}

function hasConfiguredPath(value: Record<string, unknown>, path: readonly string[]): boolean {
  let current: unknown = value;
  for (const segment of path) {
    if (!isRecord(current) || !(segment in current)) return false;
    current = current[segment];
  }
  return true;
}

function applySiliconFlowEnvironmentFallback(
  config: PiMentisConfig,
  override: Record<string, unknown> | undefined,
  environment: NodeJS.ProcessEnv,
): PiMentisConfig {
  const hasConfigured = (path: readonly string[]) =>
    override !== undefined && hasConfiguredPath(override, path);
  const configuredBaseUrl = hasConfigured(["inference", "siliconflow", "baseUrl"]);
  const configuredEmbeddingModel = hasConfigured([
    "inference",
    "siliconflow",
    "embedding",
    "model",
  ]);
  const configuredEmbeddingDimensions = hasConfigured([
    "inference",
    "siliconflow",
    "embedding",
    "dimensions",
  ]);
  const configuredRerankModel = hasConfigured(["inference", "siliconflow", "rerank", "model"]);
  const hasConfiguredRerankContext = hasConfigured([
    "inference",
    "siliconflow",
    "rerank",
    "maxInputTokens",
  ]);
  const baseUrl =
    (configuredBaseUrl
      ? undefined
      : optionalEnvironmentValue(environment, "SILICONFLOW_BASE_URL")) ??
    config.inference.siliconflow.baseUrl;
  const environmentEmbeddingModel = configuredEmbeddingModel
    ? undefined
    : optionalEnvironmentValue(environment, "SILICONFLOW_EMBEDDING_MODEL");
  const embeddingModel = environmentEmbeddingModel ?? config.inference.siliconflow.embedding.model;
  const configuredDimensions = optionalEnvironmentInteger(
    environment,
    "SILICONFLOW_EMBEDDING_DIMENSIONS",
  );
  const embeddingDimensions =
    (configuredEmbeddingDimensions ? undefined : configuredDimensions) ??
    (!configuredEmbeddingModel && environmentEmbeddingModel === "BAAI/bge-m3" ? 1_024 : undefined);
  const rerankModel =
    (configuredRerankModel
      ? undefined
      : optionalEnvironmentValue(
          environment,
          "SILICONFLOW_RERANK_MODEL",
          "SILICONFLOW_RERANKER_MODEL",
        )) ?? config.inference.siliconflow.rerank.model;
  const environmentRerankContext = optionalEnvironmentInteger(
    environment,
    "SILICONFLOW_RERANK_MAX_INPUT_TOKENS",
  );
  const rerankMaxInputTokens =
    (hasConfiguredRerankContext ? undefined : environmentRerankContext) ??
    (!configuredRerankModel && rerankModel === "BAAI/bge-reranker-v2-m3" ? 8_192 : undefined);
  return {
    ...config,
    inference: {
      ...config.inference,
      siliconflow: {
        ...config.inference.siliconflow,
        baseUrl,
        embedding: {
          ...config.inference.siliconflow.embedding,
          model: embeddingModel,
          ...(embeddingDimensions === undefined ? {} : { dimensions: embeddingDimensions }),
        },
        rerank: {
          ...config.inference.siliconflow.rerank,
          model: rerankModel,
          ...(rerankMaxInputTokens === undefined ? {} : { maxInputTokens: rerankMaxInputTokens }),
        },
      },
    },
  };
}

function applyOpenRouterEnvironmentFallback(
  config: PiMentisConfig,
  override: Record<string, unknown> | undefined,
  environment: NodeJS.ProcessEnv,
): PiMentisConfig {
  const hasConfigured = (path: readonly string[]) =>
    override !== undefined && hasConfiguredPath(override, path);
  const configuredBaseUrl = hasConfigured(["inference", "openrouter", "baseUrl"]);
  const configuredEmbeddingModel = hasConfigured(["inference", "openrouter", "embedding", "model"]);
  const configuredEmbeddingDimensions = hasConfigured([
    "inference",
    "openrouter",
    "embedding",
    "dimensions",
  ]);
  const baseUrl =
    (configuredBaseUrl
      ? undefined
      : optionalEnvironmentValue(environment, "OPENROUTER_BASE_URL")) ??
    config.inference.openrouter.baseUrl;
  const embeddingModel =
    (configuredEmbeddingModel
      ? undefined
      : optionalEnvironmentValue(environment, "OPENROUTER_EMBEDDING_MODEL")) ??
    config.inference.openrouter.embedding.model;
  const embeddingDimensions = configuredEmbeddingDimensions
    ? undefined
    : optionalEnvironmentInteger(environment, "OPENROUTER_EMBEDDING_DIMENSIONS");
  return {
    ...config,
    inference: {
      ...config.inference,
      openrouter: {
        ...config.inference.openrouter,
        baseUrl,
        embedding: {
          ...config.inference.openrouter.embedding,
          model: embeddingModel,
          ...(embeddingDimensions === undefined ? {} : { dimensions: embeddingDimensions }),
        },
      },
    },
  };
}

function resolveEmbeddingRuntime(
  configured: PiMentisConfig,
  effective: PiMentisConfig,
  override: Record<string, unknown> | undefined,
): EmbeddingRuntimeResolution {
  const providerId = effective.inference.provider;
  const configuredModel = hasConfiguredPath(override ?? {}, [
    "inference",
    providerId,
    "embedding",
    "model",
  ]);
  const configuredActive = configured.inference[providerId].embedding;
  const effectiveActive = effective.inference[providerId].embedding;
  const model = effectiveActive.model;
  const dimensions = effectiveActive.dimensions;
  const environmentSelected =
    !configuredModel &&
    (model !== configuredActive.model || dimensions !== configuredActive.dimensions);
  return {
    configured: {
      model: configuredActive.model,
      dimensions: configuredActive.dimensions,
    },
    effective: {
      providerId,
      model,
      dimensions,
    },
    source: configuredModel ? "config" : environmentSelected ? "environment" : "default",
    environmentOverrideActive: environmentSelected,
  };
}

function freezeConfig<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) freezeConfig(child);
  }
  return value;
}

function finalizeConfig(
  configured: PiMentisConfig,
  override: Record<string, unknown> | undefined,
  environment: NodeJS.ProcessEnv,
): PiMentisConfig {
  const effective = validateConfig(
    applyOpenRouterEnvironmentFallback(
      applySiliconFlowEnvironmentFallback(configured, override, environment),
      override,
      environment,
    ),
  );
  const frozen = freezeConfig(effective);
  embeddingRuntimeResolutions.set(frozen, resolveEmbeddingRuntime(configured, frozen, override));
  return frozen;
}

export async function loadConfig(
  _cwd: string,
  filename?: string,
  environment: NodeJS.ProcessEnv = process.env,
): Promise<PiMentisConfig> {
  const storageRoot = resolveStorageRoot({ environment });
  if (filename === undefined) assertStorageRootReady(storageRoot);
  const resolvedFilename = filename ?? globalConfigPath({ environment });
  const defaults = createDefaultConfig(_cwd, environment);
  let override: unknown;
  try {
    override = JSON.parse(await readFile(resolvedFilename, "utf8")) as unknown;
  } catch (error: unknown) {
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? (error as { readonly code?: unknown }).code
        : undefined;
    if (code === "ENOENT") {
      const resolved = finalizeConfig(defaults, undefined, environment);
      await mkdir(path.dirname(resolvedFilename), { recursive: true, mode: 0o700 });
      await writeFile(resolvedFilename, JSON.stringify(resolved, null, 2), { mode: 0o600 });
      return resolved;
    }
    throw new ConfigurationError(`Unable to read Pi Mentis config ${resolvedFilename}`, {
      operation: "configuration-load",
      retryable: false,
      cause: error,
    });
  }
  if (!isRecord(override)) {
    throw new ConfigurationError("Pi Mentis configuration root must be an object", {
      operation: "configuration-load",
      retryable: false,
    });
  }
  const merged = mergeConfiguration(
    defaults as unknown as Record<string, unknown>,
    override,
  ) as unknown as PiMentisConfig;
  return finalizeConfig(merged, override, environment);
}
